"use client";

import {
  showErrorToast,
  showInfoToast,
  showWarningToast,
} from "@/shared/feedback";
import { useAuth, useUserProfile } from "@/state/globalStore";
import type {
  LinkTelegramFailureResponse,
  LinkTelegramSuccessResponse,
} from "@/generated/fastapi";
import { useDisclosure } from "@chakra-ui/react";
import { useState } from "react";
import useLinkTelegramMutation from "../useLinkTelegramMutation";

export default function useTelegramLink() {
  const { accessToken } = useAuth();
  const { userProfile } = useUserProfile();
  const linkTelegramMutation = useLinkTelegramMutation(accessToken ?? undefined);

  // Helpers
  const {
    open: isTelegramLinkDialogOpen,
    onOpen: openTelegramLinkDialog,
    onClose: closeTelegramLinkDialog,
  } = useDisclosure();

  const [telegramLinkData, setTelegramLinkData] =
    useState<LinkTelegramSuccessResponse | null>(null);

  // Check if Telegram is linked
  const isTelegramLinked = Boolean(userProfile?.data.telegram_id);

  async function handleTelegramLinkClick() {
    try {
      const backendResponse = await linkTelegramMutation.mutateAsync();

      if ("link_url" in backendResponse) {
        const formattedResponse: LinkTelegramSuccessResponse = {
          status: backendResponse.status,
          link_url: backendResponse.link_url,
          token: backendResponse.token,
          expires_in: backendResponse.expires_in,
        };

        setTelegramLinkData(formattedResponse);
        openTelegramLinkDialog();
      } else if (backendResponse.status === "conflict") {
        showWarningToast("Telegram account already linked");
        showInfoToast("If needed, unlink your account using Telegram");
        setTelegramLinkData(null);
      } else {
        showErrorToast("Invalid response from server");
        setTelegramLinkData(null);
        return {
          status: "error",
          message: "Invalid response from server",
        } as LinkTelegramFailureResponse;
      }
    } catch (error) {
      const errorMessage = "Failed to link Telegram account";

      if (error instanceof Error) {
        showErrorToast(error.message);
      } else if (typeof error === "string") {
        showErrorToast(error);
      } else {
        showErrorToast(errorMessage);
      }
    }
  }

  return {
    isTelegramLinkDialogOpen,
    closeTelegramLinkDialog,
    isLoading: linkTelegramMutation.isPending,
    telegramLinkData,
    handleTelegramLinkClick,
    isTelegramLinked,
  };
}
